import axios from 'axios';
import { isPolygonGeometry } from '../components/crop-intelligence/contract';

export const CC_PAGE_SIZE = 5000;
export const CC_FEATURE_BUDGET = 20000;

export function ccGeoserverOrigin() {
  const value = process.env.REACT_APP_CC_GEOSERVER_URL;
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password ||
        url.pathname !== '/' || url.search || url.hash) return null;
    return url.origin;
  } catch (error) { return null; }
}

export function parseFeaturePage(data) {
  if (typeof data === 'string') {
    // GeoServer can send an XML ExceptionReport with HTTP 200.
    if (/<(?:\w+:)?(?:ExceptionReport|ServiceExceptionReport|Exception|ServiceException)\b/i.test(data)) {
      throw new Error('GeoServer service exception');
    }
    try { data = JSON.parse(data); } catch (error) { throw new Error('Invalid GeoServer response'); }
  }
  if (data?.type !== 'FeatureCollection' || !Array.isArray(data.features)) throw new Error('Invalid GeoServer response');
  return data;
}

export async function getCCViewport({ dataset, bbox, signal, request = axios.get }) {
  const origin = ccGeoserverOrigin();
  if (!origin) throw new Error('CC GeoServer origin is not configured');
  const features = new Map();
  let offset = 0;
  let matched;
  let uncertain = false;
  let pages = 0;
  while (offset < CC_FEATURE_BUDGET && pages < 40) {
    pages += 1;
    if (signal?.aborted) throw new axios.CanceledError();
    const count = Math.min(CC_PAGE_SIZE, CC_FEATURE_BUDGET - offset);
    // Use the same public/proxied origin as image tiles. Do not forward the
    // application bearer token to a separate GeoServer host.
    const response = await request(`${origin}/geoserver/ows`, {
      params: { service: 'WFS', version: '2.0.0', request: 'GetFeature', typeNames: dataset.layer,
        outputFormat: 'application/json', srsName: 'EPSG:4326',
        bbox: `${bbox.join(',')},EPSG:4326`, sortBy: 'fid A', count, startIndex: offset },
      signal, timeout: 30000,
    });
    if (signal?.aborted) throw new axios.CanceledError();
    const page = parseFeaturePage(response.data);
    if (page.features.length > count) throw new Error('GeoServer ignored page limit');
    const known = Number.isInteger(page.numberMatched) && page.numberMatched >= 0;
    if (!known || (matched !== undefined && matched !== page.numberMatched)) uncertain = true;
    if (known) matched = page.numberMatched;
    if (!page.features.length) {
      return { features: [...features.values()], incomplete: uncertain || matched !== offset };
    }
    const previousSize = features.size;
    for (const feature of page.features) {
      const p = feature.properties;
      const id = p?.fid || feature.id;
      if (typeof id !== 'string' || !id || p?.year !== dataset.year || p?.country_code !== 'TR' ||
          p?.class_schema !== dataset.class_schema || !isPolygonGeometry(feature.geometry)) {
        throw new Error('Invalid CC feature identity, scope or geometry');
      }
      if (features.has(id)) uncertain = true;
      features.set(id, feature);
    }
    offset += page.features.length;
    if (known && offset > matched) uncertain = true;
    if (features.size === previousSize) return { features: [...features.values()], incomplete: true };
    // Probe even after a short page: server caps can be smaller than count.
    // Only an empty page plus consistent counts establishes completion.
  }
  return { features: [...features.values()], incomplete: true };
}
